import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  EyeIcon,
  PencilIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

/**
 * Dashboard for Content Reviewer role
 * Shows questions for review, review statistics, and workflow status
 */
const ContentReviewerDashboard = () => {
  const [stats, setStats] = useState({
    totalQuestionsReviewed: 0,
    assignedQuestions: 0,
    readyToShipQuestions: 0,
    questionsWithIssues: 0
  });
  const [questionsForReview, setQuestionsForReview] = useState([]);
  const [assignedQuestions, setAssignedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch Content Reviewer statistics and questions
      const [statsResponse, reviewQuestionsResponse, assignedQuestionsResponse] = await Promise.all([
        api.get('/api/super-admin/content-reviewer/stats'),
        api.get('/api/super-admin/content-reviewer/questions-for-review?page=0&size=5'),
        api.get('/api/super-admin/content-reviewer/assigned-questions?page=0&size=5')
      ]);

      setStats(statsResponse.data);
      setQuestionsForReview(reviewQuestionsResponse.data.content || []);
      setAssignedQuestions(assignedQuestionsResponse.data.content || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickReview = async (questionId, status, comments = '') => {
    try {
      await api.post(`/api/super-admin/content-reviewer/questions/${questionId}/status`, {
        status,
        reviewComments: comments
      });
      
      // Refresh data after review
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating question status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const quickActions = [
    { name: 'Review Questions', href: '/questions-for-review', icon: EyeIcon, color: 'bg-blue-500' },
    { name: 'My Assignments', href: '/assigned-questions', icon: DocumentTextIcon, color: 'bg-purple-500' },
    { name: 'Approved Questions', href: '/questions-for-review?status=READY_TO_SHIP', icon: CheckCircleIcon, color: 'bg-green-500' },
    { name: 'Questions with Issues', href: '/questions-for-review?status=RENDERING_FAILURE,DUPLICATE,NOT_USABLE', icon: ExclamationTriangleIcon, color: 'bg-red-500' }
  ];

  const reviewActions = [
    { status: 'READY_TO_SHIP', label: 'Approve', color: 'bg-green-500 hover:bg-green-600', icon: CheckCircleIcon },
    { status: 'RENDERING_FAILURE', label: 'Rendering Issue', color: 'bg-yellow-500 hover:bg-yellow-600', icon: ExclamationTriangleIcon },
    { status: 'DUPLICATE', label: 'Duplicate', color: 'bg-orange-500 hover:bg-orange-600', icon: XCircleIcon },
    { status: 'NOT_USABLE', label: 'Not Usable', color: 'bg-red-500 hover:bg-red-600', icon: XCircleIcon }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Content Reviewer Dashboard</h1>
        <p className="text-gray-600 mt-1">Review and approve questions for publication</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className="group relative bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center">
              <div className={`${action.color} p-3 rounded-lg`}>
                <action.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                  {action.name}
                </h3>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-2xl font-bold text-gray-900">{stats.totalQuestionsReviewed}</div>
          <div className="text-sm text-gray-600">Total Reviewed</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-2xl font-bold text-blue-600">{stats.assignedQuestions}</div>
          <div className="text-sm text-gray-600">Assigned to Me</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-2xl font-bold text-green-600">{stats.readyToShipQuestions}</div>
          <div className="text-sm text-gray-600">Approved</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-2xl font-bold text-red-600">{stats.questionsWithIssues}</div>
          <div className="text-sm text-gray-600">Flagged Issues</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Questions for Review */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Questions for Review</h3>
              <Link to="/questions-for-review" className="text-sm text-blue-600 hover:text-blue-800">
                View all →
              </Link>
            </div>
          </div>
          <div className="p-6">
            {questionsForReview.length > 0 ? (
              <div className="space-y-4">
                {questionsForReview.map((question) => (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="mb-3">
                      <div className="font-medium text-gray-900 mb-1">
                        {question.questionText.substring(0, 80)}
                        {question.questionText.length > 80 && '...'}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>{question.subject}</span>
                        <span>•</span>
                        <span>{question.difficultyLevel}</span>
                        <span>•</span>
                        <span>by {question.createdBy}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleQuickReview(question.id, 'READY_TO_SHIP', 'Quick approval')}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-green-500 hover:bg-green-600 rounded"
                        >
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleQuickReview(question.id, 'RENDERING_FAILURE', 'Needs revision')}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded"
                        >
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                          Issue
                        </button>
                      </div>
                      <Link
                        to={`/questions/${question.id}/review`}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Review
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No questions pending review</p>
            )}
          </div>
        </div>

        {/* Assigned Questions */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">My Assigned Questions</h3>
              <Link to="/assigned-questions" className="text-sm text-blue-600 hover:text-blue-800">
                View all →
              </Link>
            </div>
          </div>
          <div className="p-6">
            {assignedQuestions.length > 0 ? (
              <div className="space-y-4">
                {assignedQuestions.map((question) => (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="mb-3">
                      <div className="font-medium text-gray-900 mb-1">
                        {question.questionText.substring(0, 80)}
                        {question.questionText.length > 80 && '...'}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>{question.subject}</span>
                        <span>•</span>
                        <span>{question.difficultyLevel}</span>
                        <span>•</span>
                        <span>assigned {new Date(question.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        {question.status === 'PENDING' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        )}
                        {question.status === 'READY_TO_SHIP' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Approved
                          </span>
                        )}
                      </div>
                      <Link
                        to={`/questions/${question.id}/review`}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Review
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No questions assigned</p>
            )}
          </div>
        </div>
      </div>

      {/* Review Guidelines */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-green-900 mb-3">📋 Review Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-800">
          <div>
            <h4 className="font-medium mb-2">Quality Checklist</h4>
            <ul className="space-y-1">
              <li>• Question clarity and grammar</li>
              <li>• Correct answer verification</li>
              <li>• Appropriate difficulty level</li>
              <li>• LaTeX/image rendering</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Review Actions</h4>
            <ul className="space-y-1">
              <li>• <strong>Approve:</strong> Ready for publication</li>
              <li>• <strong>Rendering Issue:</strong> LaTeX/image problems</li>
              <li>• <strong>Duplicate:</strong> Similar question exists</li>
              <li>• <strong>Not Usable:</strong> Quality issues</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quick Review Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Review Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {reviewActions.map((action) => (
            <div key={action.status} className="text-center">
              <div className={`${action.color} p-3 rounded-lg inline-flex items-center justify-center mb-2`}>
                <action.icon className="h-6 w-6 text-white" />
              </div>
              <div className="text-sm font-medium text-gray-900">{action.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentReviewerDashboard;
